import { useState, useRef, useEffect } from "react";
import { BookOpen, ChevronLeft, ArrowRight, Volume2, Loader2, Square, Check, CheckCircle2, GraduationCap, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { COURSES } from "@/data/teachings";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Teachings() {
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const premium = user?.tier === "premium";
  const [courseId, setCourseId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [completed, setCompleted] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/teachings/progress").then((res) => setCompleted(res.data.completed || [])).catch(() => {});
    return () => stopAudio();
  }, []);

  function stopAudio() {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingKey(null);
    setLoadingKey(null);
  }

  const toggleComplete = async (lessonId) => {
    const isDone = completed.includes(lessonId);
    try {
      const { data } = await api.post("/teachings/progress", { lesson_id: lessonId, completed: !isDone });
      setCompleted(data.completed || []);
      toast.success(isDone ? "Marked as not complete" : "Lesson marked complete");
    } catch (e) {
      toast.error("Could not update progress.");
    }
  };

  const listen = async (key, text) => {
    if (playingKey === key) { stopAudio(); return; }
    stopAudio();
    setLoadingKey(key);
    try {
      const { data } = await api.post("/tts", { text: text.slice(0, 4000) });
      const a = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = a;
      a.onended = () => setPlayingKey(null);
      await a.play();
      setPlayingKey(key);
    } catch (e) {
      toast.error("Could not play audio. Please try again.");
    } finally {
      setLoadingKey(null);
    }
  };

  const doneInCourse = (c) => c.lessons.filter((l) => completed.includes(l.id)).length;

  const course = COURSES.find((c) => c.id === courseId);
  const lesson = course?.lessons.find((l) => l.id === activeId);

  // -------- Lesson detail --------
  if (course && lesson) {
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 pb-24">
        <button
          data-testid="teachings-back"
          onClick={() => { stopAudio(); setActiveId(null); }}
          className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300 mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> All lessons
        </button>

        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">{course.course}</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">{lesson.title}</h1>
        <p className="font-decree text-lg text-[#4A4744] mt-4">{lesson.summary}</p>

        <button
          data-testid="toggle-complete"
          onClick={() => toggleComplete(lesson.id)}
          className={`mt-6 flex items-center gap-2 px-5 py-2.5 text-sm rounded-sm transition-all duration-300 ${
            completed.includes(lesson.id)
              ? "bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90"
              : "border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF]"
          }`}
        >
          {completed.includes(lesson.id) ? <CheckCircle2 className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {completed.includes(lesson.id) ? "Completed" : "Mark lesson complete"}
        </button>

        <Accordion type="single" collapsible className="mt-10" defaultValue="s-0">
          {lesson.sections.map((s, i) => {
            const key = `${lesson.id}-${i}`;
            const ttsText = `${s.title}. ${s.body}`;
            return (
              <AccordionItem key={key} value={`s-${i}`} data-testid={`teaching-section-${i}`} className="border-b border-[#D9D4CC]">
                <AccordionTrigger className="font-heading text-lg sm:text-xl text-[#2C2A29] text-left hover:no-underline py-5">
                  {s.title}
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <button
                    data-testid={`teaching-listen-${i}`}
                    onClick={() => listen(key, ttsText)}
                    className="flex items-center gap-2 px-3.5 py-2 mb-5 text-sm border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
                  >
                    {loadingKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : playingKey === key ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    {playingKey === key ? "Stop" : "Listen"}
                  </button>

                  <p className="font-body text-base leading-relaxed text-[#4A4744]">{s.body}</p>

                  {s.points?.length > 0 && (
                    <ul className="mt-6 space-y-2.5">
                      {s.points.map((p, j) => (
                        <li key={j} className="flex gap-3 font-body text-base text-[#2C2A29]">
                          <span className="text-[#B7AE9F] mt-2 w-1.5 h-1.5 rounded-full bg-[#B7AE9F] shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {s.scriptures?.length > 0 && (
                    <div className="mt-6">
                      <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Scriptures</p>
                      <div className="flex flex-wrap gap-2">
                        {s.scriptures.map((ref) => (
                          <span key={ref} className="px-3 py-1.5 text-sm font-decree italic border border-[#D9D4CC] text-[#4A4744] rounded-full">
                            {ref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    );
  }

  // -------- Lessons index for a course --------
  if (course) {
    const done = doneInCourse(course);
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="teachings-course">
        <button
          data-testid="courses-back"
          onClick={() => setCourseId(null)}
          className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300 mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> All courses
        </button>
        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
          <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Teachings · {course.ministry}</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#2C2A29] tracking-tight">{course.course}</h1>
        <p className="font-decree text-xl text-[#4A4744] mt-4 leading-relaxed">{course.description}</p>

        <div className="mt-8" data-testid="teachings-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="font-body text-sm text-[#4A4744]">Your progress</p>
            <p className="font-body text-sm text-[#7A756E]">{done} of {course.lessons.length} lessons</p>
          </div>
          <div className="h-2 bg-[#EAE6DF] rounded-full overflow-hidden">
            <div className="h-full bg-[#2C2A29] transition-all duration-500" style={{ width: `${(done / course.lessons.length) * 100}%` }} />
          </div>
        </div>

        <ul className="mt-10 space-y-3">
          {course.lessons.map((l, idx) => {
            const locked = !premium && idx > 0;
            return (
            <li key={l.id}>
              <button
                data-testid={`lesson-card-${l.id}`}
                onClick={() => locked
                  ? openPaywall("Free accounts can read the first lesson of each course. Go Premium to unlock the full teaching library.")
                  : setActiveId(l.id)}
                className={`group w-full text-left flex items-center justify-between gap-4 px-6 py-6 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm transition-all duration-300 ${locked ? "opacity-80 hover:border-[#9B7E4E]" : "hover:bg-[#EAE6DF] hover:border-[#B7AE9F]"}`}
              >
                <div>
                  <div className="flex items-center gap-2.5">
                    {completed.includes(l.id) && <CheckCircle2 className="w-5 h-5 text-[#2C2A29] shrink-0" />}
                    <p className="font-heading text-xl text-[#2C2A29]">{l.title}</p>
                    {locked && (
                      <span data-testid={`lesson-locked-${l.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-[#9B7E4E] text-[#9B7E4E]">
                        <Crown className="w-3 h-3" /> Premium
                      </span>
                    )}
                  </div>
                  <p className="font-body text-sm text-[#7A756E] mt-1.5">{l.summary}</p>
                  <p className="font-body text-xs uppercase tracking-wide text-[#B7AE9F] mt-2">
                    {completed.includes(l.id) ? "Completed" : `${l.sections.length} ${l.sections.length === 1 ? "section" : "sections"}`}
                  </p>
                </div>
                {locked
                  ? <Lock className="w-5 h-5 text-[#9B7E4E] shrink-0" strokeWidth={1.75} />
                  : <ArrowRight className="w-5 h-5 text-[#B7AE9F] group-hover:text-[#2C2A29] group-hover:translate-x-1 transition-all duration-300 shrink-0" />}
              </button>
            </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // -------- Course selection --------
  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="teachings-page">
      <div className="flex items-center gap-3 mb-6">
        <GraduationCap className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Teachings · War-Zone Global Ministry</p>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl text-[#2C2A29] tracking-tight">Courses</h1>
      <p className="font-decree text-xl text-[#4A4744] mt-4 leading-relaxed">
        Grow in the Word through structured, Spirit-led teaching. Choose a course to begin.
      </p>

      <ul className="mt-10 space-y-4">
        {COURSES.map((c) => {
          const done = doneInCourse(c);
          const pct = Math.round((done / c.lessons.length) * 100);
          return (
            <li key={c.id}>
              <button
                data-testid={`course-card-${c.id}`}
                onClick={() => { setCourseId(c.id); setActiveId(null); }}
                className="group w-full text-left px-6 py-6 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm hover:bg-[#EAE6DF] hover:border-[#B7AE9F] transition-all duration-300"
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="font-heading text-2xl text-[#2C2A29]">{c.course}</p>
                  <ArrowRight className="w-5 h-5 text-[#B7AE9F] group-hover:text-[#2C2A29] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                </div>
                <p className="font-body text-sm text-[#7A756E] mt-2 leading-relaxed">{c.description}</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-[#EAE6DF] rounded-full overflow-hidden">
                    <div className="h-full bg-[#9B7E4E] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-body text-xs text-[#7A756E] shrink-0">{done}/{c.lessons.length} lessons</span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
